import React from 'react';
import {
  Box, Typography, Button, Paper, Avatar
} from '@mui/material';
import { 
  BarChart3, DollarSign, TrendingUp, TrendingDown, 
  ExternalLink, Eye
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Sheet URLs
const SHEET_URLS = {
  dashboard: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRrSasZKikM06ob9ai6SGsqKvM0RsrVmdLaRsp-WLpwjN-9us1WJzbt0nlYzcs4fJcrwcLAnR9B3wmg/pubhtml?gid=1891769276&single=true',
  payouts: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRrSasZKikM06ob9ai6SGsqKvM0RsrVmdLaRsp-WLpwjN-9us1WJzbt0nlYzcs4fJcrwcLAnR9B3wmg/pubhtml?gid=94746379&single=true',
  payouts1x: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRrSasZKikM06ob9ai6SGsqKvM0RsrVmdLaRsp-WLpwjN-9us1WJzbt0nlYzcs4fJcrwcLAnR9B3wmg/pubhtml?gid=1732707070&single=true',
  payouts2x: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vRrSasZKikM06ob9ai6SGsqKvM0RsrVmdLaRsp-WLpwjN-9us1WJzbt0nlYzcs4fJcrwcLAnR9B3wmg/pubhtml?gid=1051204904&single=true'
};

const EDIT_URL = 'https://docs.google.com/spreadsheets/d/1U24malBQvx5zr-cBnshG_Zt-a09E1G5ZUbDNjPjB6N8/edit?gid=1666785029#gid=1666785029';

export default function SheetsViewer() {
  const { user } = useAuth();

  const openSheet = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openEditSheet = () => {
    window.open(EDIT_URL, '_blank', 'noopener,noreferrer');
  };

  // Check permissions
  if (user?.role_id !== 3 && user?.role_id !== 2) {
    return (
      <Box p={2} display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Access Denied
          </Typography>
          <Typography color="text.secondary">
            Only administrators and managers can access this page.
          </Typography>
        </Paper>
      </Box>
    );
  }

  const sheets = [
    { 
      title: 'Dashboard', 
      icon: BarChart3, 
      color: '#1976d2',
      url: SHEET_URLS.dashboard
    },
    { 
      title: 'Payouts', 
      icon: DollarSign, 
      color: '#2e7d32',
      url: SHEET_URLS.payouts
    },
    { 
      title: 'Payouts 1x', 
      icon: TrendingUp, 
      color: '#ed6c02',
      url: SHEET_URLS.payouts1x
    },
    { 
      title: 'Payouts 2x', 
      icon: TrendingDown, 
      color: '#d32f2f',
      url: SHEET_URLS.payouts2x
    },
    { 
      title: 'Edit Sheet', 
      icon: ExternalLink, 
      color: '#1976d2',
      url: EDIT_URL,
      isEdit: true
    }
  ];

  const SheetItem = ({ sheet }) => {
    const IconComponent = sheet.icon;
    
    return (
      <Paper 
        elevation={0}
        sx={{ 
          mb: 1.5, 
          p: 2, 
          display: 'flex', 
          alignItems: 'center',
          border: '1px solid #e0e0e0',
          borderRadius: 2,
          height: '80px',
          cursor: 'pointer',
          '&:hover': { 
            borderColor: '#1976d2',
            transform: 'translateY(-1px)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          },
          transition: 'all 0.2s ease-in-out'
        }}
        onClick={() => sheet.isEdit ? openEditSheet() : openSheet(sheet.url)}
      >
        <Avatar 
          sx={{ 
            bgcolor: sheet.color, 
            mr: 2,
            width: 40,
            height: 40
          }}
        >
          <IconComponent size={20} color="white" />
        </Avatar>
        
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="body1" fontWeight="600" color="text.primary">
            {sheet.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Click to {sheet.isEdit ? 'edit' : 'open'} sheet
          </Typography>
        </Box>
        
        <Button
          variant="contained"
          startIcon={sheet.isEdit ? <ExternalLink size={16} /> : <Eye size={16} />}
          sx={{
            fontWeight: 'bold',
            background: sheet.isEdit 
              ? 'linear-gradient(to right, #1976d2, #0d47a1)'
              : sheet.color,
            '&:hover': {
              background: sheet.isEdit 
                ? 'linear-gradient(to right, #1565c0, #003c8f)'
                : sheet.color,
              filter: 'brightness(0.9)'
            }
          }}
        >
          {sheet.isEdit ? 'Edit' : 'Open'}
        </Button>
      </Paper>
    );
  };

  return (
    <Box p={1}>
      <Box sx={{ 
        maxWidth: 1000, 
        margin: '0 auto'
      }}>
        {sheets.map((sheet, index) => (
          <SheetItem key={index} sheet={sheet} />
        ))}
      </Box>
    </Box>
  );
}